// src/hooks/useAsinMonthlyResults.ts

import { useState, useEffect, useCallback } from 'react';
import { subscribeToAsinMonthlyResults } from '@/services/asinMonthlyResultsService';
import { AsinMonthlyData, UseAsinMonthlyResultsReturn } from '@/types/asinMonthlyResultsTypes';

/**
 * Custom hook for fetching and managing ASIN monthly results data
 * Provides real-time updates using Firestore onSnapshot
 * 
 * @param companyName - The company name (optional)
 * @param asin - The ASIN identifier (optional)
 * @returns Object containing data, loading state, and error state
 */
export const useAsinMonthlyResults = (
  companyName?: string,
  asin?: string
): UseAsinMonthlyResultsReturn => {
  const [data, setData] = useState<AsinMonthlyData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Callback for handling data updates
  const handleDataUpdate = useCallback((newData: AsinMonthlyData | null) => {
    setData(newData);
    setIsLoading(false);
    setError(null);
  }, []);

  // Callback for handling errors
  const handleError = useCallback((newError: Error) => {
    setError(newError);
    setIsLoading(false);
    setData(null);
  }, []);

  useEffect(() => {
    // Reset state when parameters change
    setData(null);
    setError(null);

    // Don't fetch if required parameters are missing
    if (!companyName || !asin) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Subscribe to real-time updates
    const unsubscribe = subscribeToAsinMonthlyResults(
      companyName,
      asin,
      handleDataUpdate,
      handleError
    );

    // Cleanup subscription on unmount or parameter change
    return () => {
      unsubscribe();
    };
  }, [companyName, asin, handleDataUpdate, handleError]);

  return {
    data,
    isLoading,
    error,
  };
};

/**
 * Hook variant that only loads data when explicitly requested
 * Useful for scenarios where you want to control when data is fetched
 */
export const useAsinMonthlyResultsLazy = () => {
  const [data, setData] = useState<AsinMonthlyData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async (companyName: string, asin: string) => {
    if (!companyName || !asin) {
      setError(new Error('Company name and ASIN are required'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { fetchAsinMonthlyResults } = await import('@/services/asinMonthlyResultsService');
      const result = await fetchAsinMonthlyResults(companyName, asin);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    data,
    isLoading,
    error,
    fetchData,
    reset,
  };
};